import { memo, useEffect, useRef } from 'react'
import { DOMSerializer } from 'prosemirror-model'
import cx from 'classnames'
import { parseRichText } from './richTextDoc'
import { textboxSchema } from './textboxSchema'
import Styles from './TextBoxShape.module.scss'

interface Props {
  className?: string
  // Accepts null/undefined and malformed docs; parseRichText guards and renders nothing if unusable.
  richText?: Record<string, unknown> | null
  // When set (the background layer), each block's inline text is wrapped in a span with this class so a
  // silhouette highlight can hug each wrapped line via box-decoration-break. Omitted for the visible and
  // stroke layers.
  highlightClassName?: string
}

// Bullets cycle per depth. KEEP IN LOCKSTEP with the editor CSS (`.richText` `$bullets` in
// TextBoxShape.module.scss) and monospace's richTextBlockLayout.ts BULLETS — a mismatch shows a
// different glyph in the preview vs the final render.
const BULLETS = ['•', '◦', '▪']

// Compute the marker glyph for every list item, in document order, mirroring the CSS counters that draw
// the VISIBLE marker (`.richText ol/ul li::before`) and the monospace renderer's `nextOrderedCount`.
// Ordered lists number decimal at every depth; a shallower item restarts the deeper counters; each list
// element restarts its own counters. Stashed on each <li> as `data-marker` for the highlight pass to
// inline into the span (see below).
const stashListMarkers = (container: HTMLElement) => {
  container.querySelectorAll('ol, ul').forEach((list) => {
    const ordered = list.tagName === 'OL'
    const counters: number[] = []
    list.querySelectorAll(':scope > li').forEach((li) => {
      const depth = parseInt(li.getAttribute('data-depth') ?? '0', 10) || 0
      let marker: string
      if (ordered) {
        counters[depth] = (counters[depth] ?? 0) + 1
        counters.length = depth + 1 // a shallower item restarts the deeper levels
        marker = `${counters[depth]}.`
      } else {
        marker = BULLETS[depth % BULLETS.length]
      }
      li.setAttribute('data-marker', marker)
    })
  })
}

export const RichTextContent = memo(({ className, richText, highlightClassName }: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = ref.current
    if (!container) return
    container.replaceChildren()
    // Guard: an unusable doc renders nothing rather than throwing in the effect (the render
    // decision falls back to plain text upstream; this protects the canvas if one slips through).
    const doc = parseRichText(richText)
    if (!doc) return
    // The scoped schema forces no trailing paragraph, so there is nothing phantom to strip — serialize
    // the doc as-is.
    container.appendChild(DOMSerializer.fromSchema(textboxSchema).serializeFragment(doc.content))

    // Background layer only: wrap each paragraph's inline content in a highlight span so the silhouette
    // background follows the text per line (box-decoration-break) instead of boxing the whole block. For
    // list items the marker is inlined into the SAME span (as an inline-block sized like the CSS gutter)
    // so the marker and text share ONE box model — the visible `::before` marker is a block flex item
    // whose box never matched the inline text highlight, which is what we're replacing. The `::before`
    // gutter is hidden in the background layer via CSS (`.textBoxBackground li::before`), and the `li`
    // indent is kept, so the inlined marker lands exactly where the visible marker sits.
    if (highlightClassName) {
      stashListMarkers(container)
      container.querySelectorAll('p').forEach((paragraph) => {
        const li = paragraph.closest('li')
        const marker = li?.getAttribute('data-marker')
        // Skip empty non-list lines (nothing to highlight); empty list items still get their marker box.
        if (!paragraph.firstChild && !marker) return
        const span = document.createElement('span')
        span.className = highlightClassName
        while (paragraph.firstChild) span.appendChild(paragraph.firstChild)
        if (marker) {
          const markerEl = document.createElement('span')
          markerEl.className = Styles.silhouetteMarker
          markerEl.textContent = marker
          span.insertBefore(markerEl, span.firstChild)
        }
        paragraph.appendChild(span)
      })
    }

    // A bare <p></p> establishes no line box, so an empty paragraph (an intentional blank line) would
    // collapse to zero height here while measureRichText and the monospace render count a full line.
    // Mirror ProseMirror's trailing-break: give it a <br>. After the highlight pass, so an empty line
    // still gets no highlight span (no empty pill) — the <br> only makes it occupy its line.
    container.querySelectorAll('p').forEach((paragraph) => {
      if (!paragraph.firstChild) paragraph.appendChild(document.createElement('br'))
    })
  }, [richText, highlightClassName])

  return <div ref={ref} className={cx(className, Styles.richText)} />
})

RichTextContent.displayName = 'RichTextContent'
